import { Alert, Card, IconButton, Modal, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import close2 from '../../../icons/close2.svg';
import { LoadingButton } from '@mui/lab';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeApplicationStatus, selectApplicationsState, sendApplicationDecisionNotification } from '../../../resui/applications/sagas/pm/applicationsSlice';
import { ApplicationSummaryDto, ChangeApplicationStatusEnum, SendApplicationStatusNotificationDto } from '@inosio/payments-sdk';
import { htmlEncode } from '../../../shared/util/converters';

export interface ReturnApplicationModalProps {
  open: boolean;
  onClose: () => void;
  application: ApplicationSummaryDto | undefined;
}
export default function ReturnApplicationModal(props: ReturnApplicationModalProps) {
  const styles = {
    card: {
      position: 'absolute' as 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      padding: '16px',
      height: '523px',
      pointerEvents: 'auto',
      width: '98%',
      maxWidth: '472px',
      borderRadius: '24px',
      outline: 'none',
    },
    buttonContained: { borderRadius: '24px', fontFamily: 'Work Sans !important', textTransform: 'capitalize', color: 'white', backgroundColor: '#39A2DB', borderColor: '#39A2DB', width: '130px' },
    returnReasonText: {
      fontFamily: 'Work Sans !important',
      fontStyle: 'normal !important',
      fontWeight: '400 !important',
      fontSize: '14px !important',
      lineHeight: '22px !important',
      color: '#000000 !important',
    },
  };
  const dispatch = useDispatch();

  const [returnReasonDescription, setReturnReasonDescription] = useState('');
  const [failureSnackBarOpen, setFailureSnackBarOpen] = useState(false);
  const {
    isChangingApplicationStatusSuccess,
    isChangingApplicationStatusFailed,
    isChangingApplicationStatus,
    isSendingApplicationDecisionNotification,
    isSendingApplicationDecisionNotificationFailure,
    isSendingApplicationDecisionNotificationSuccess,
  } = useSelector(selectApplicationsState);
  const [returnAppError, setReturnAppError] = useState('');

  const handleSend = () => {
    if (returnReasonDescription.length > 0) {
      //encode the input before sending it
      const sanitisedReturnReason = htmlEncode(returnReasonDescription);
      dispatch({
        type: changeApplicationStatus.type,
        payload: {
          applicationUuid: props.application?.Uuid,
          changeAppStatusDto: {
            Status: ChangeApplicationStatusEnum.Return,
            ReturnReason: sanitisedReturnReason,
          },
        },
      });
    }
  };

  const handleClose = () => {
    props.onClose();
    setReturnReasonDescription('');
  };

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setFailureSnackBarOpen(false);
  };

  useEffect(() => {
    if (isChangingApplicationStatusSuccess && returnReasonDescription.length > 0) {
      const recipientUuids = props.application?.Applicants.map((applicant) => applicant.Uuid);

      setReturnAppError('');
      setFailureSnackBarOpen(false);

      dispatch({
        type: sendApplicationDecisionNotification.type,
        payload: {
          applicationUuid: props.application?.Uuid,
          sendApplicationStatusNotificationDto: {
            RecipientUuids: recipientUuids,
          } as SendApplicationStatusNotificationDto,
        },
      });
    } else if (isChangingApplicationStatusFailed) {
      setFailureSnackBarOpen(true);
      setReturnAppError('Apologies, an error occurred while returning the application. Please try again later or contact support. Thank you');
    }
  }, [isChangingApplicationStatusSuccess, isChangingApplicationStatusFailed]);

  useEffect(() => {
    if (isSendingApplicationDecisionNotificationSuccess) {
      setReturnAppError('');
      setFailureSnackBarOpen(false);
      handleClose();
    } else if (isSendingApplicationDecisionNotificationFailure) {
      setFailureSnackBarOpen(true);
      setReturnAppError('Apologies, an error occurred while emailing the applicant. Please try again later or contact support. Thank you');
    }
  }, [isSendingApplicationDecisionNotificationSuccess, isSendingApplicationDecisionNotificationFailure]);

  return (
    <Box>
      <Modal
        open={props.open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        BackdropProps={{
          invisible: false,
        }}
      >
        <Card sx={styles.card}>
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ position: 'relative', mt: 1 }}>
            <Typography
              sx={{
                fontFamily: '"Work Sans",Sans serif',
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '22px',
                color: '#14142B',
                marginLeft: '0 !important',
              }}
            >
              Provide comments for the Applicant(s)
            </Typography>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Close modal"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 0,
              }}
            >
              <img src={close2} alt="close" />
            </IconButton>
          </Stack>
          <Stack
            mt={6}
            pl={2}
            pr={2}
            sx={{
              '& fieldset': {
                borderRadius: '24px',
                borderColor: 'white',
              },
            }}
          >
            <TextField
              multiline
              value={returnReasonDescription}
              onChange={(e) => {
                setReturnReasonDescription(e.target.value);
              }}
              rows={12}
              sx={{ width: '100%', backgroundColor: '#F1F0F5', borderRadius: '24px' }}
              placeholder={'Describe what you’d like the applicant to add or be different about their application...'}
              error={false}
              inputProps={{ maxLength: 500 }}
            />
            <Typography mt={2} style={styles.returnReasonText} sx={{ textAlign: 'left', color: returnReasonDescription.length === 500 ? '#DB3939 !important' : '#14142B !important' }}>{`${
              500 - returnReasonDescription.length
            } characters left.`}</Typography>
          </Stack>

          <Stack spacing={2} mt={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end' }}>
            <LoadingButton
              loading={isChangingApplicationStatus || isSendingApplicationDecisionNotification}
              disabled={returnReasonDescription.length === 0 || isChangingApplicationStatus || isSendingApplicationDecisionNotification}
              variant="contained"
              loadingPosition="end"
              sx={styles.buttonContained}
              onClick={handleSend}
            >
              Send
            </LoadingButton>
          </Stack>
        </Card>
      </Modal>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={failureSnackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {returnAppError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
